import React, { useEffect, useRef, useState } from 'react';
import { Info, Eye, EyeOff, PencilLine, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import type { AlternativeMethodCallbacks } from '../../types/verification';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import TryAnotherWay from '../ui/TryAnotherWay';
import ActionLink from '../ui/ActionLink';

type KbaSsnFormProps = AlternativeMethodCallbacks & {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onSubmit: (data: Partial<FormData>) => void;
  methodExhausted?: boolean;
  onUpdatePersonalInfo?: () => void;
  onUpdateBirthDate?: () => void;
};

export default function KbaSsnForm({
  formData,
  loading,
  error,
  onSubmit,
  methodExhausted = false,
  onUpdatePersonalInfo,
  onUpdateBirthDate,
  onChooseSms,
  onChooseProspect,
}: KbaSsnFormProps) {
  const { t, i18n } = useTranslation('wizard');
  const inputRef = useRef<HTMLInputElement>(null);
  const [ssn, setSsn] = useState(formData.fullSsn || '');
  const [showSsn, setShowSsn] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    setSsn(digits);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidSsn(ssn)) return;
    onSubmit({ fullSsn: ssn });
  };

  const isValidSsn = (value: string) => /^\d{9}$/.test(value);

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map((n) => parseInt(n, 10));
    const date = new Date(year, month - 1, day);
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatSsnDisplay = (value: string) => {
    if (showSsn) {
      const p1 = value.slice(0, 3);
      const p2 = value.slice(3, 5);
      const p3 = value.slice(5, 9);
      if (value.length <= 3) return p1;
      if (value.length <= 5) return `${p1}-${p2}`;
      return `${p1}-${p2}-${p3}`;
    }
    return value.replace(/./g, '•').slice(0, value.length);
  };

  return (
    <form
      id="kba-ssn-form"
      name="kba-ssn-form"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="text-center">
        <Title
          id="kba-ssn-title"
          name="kba-ssn-title"
          as="h1"
          className="mb-2"
        >
          {t('kbaSsn.title')}
        </Title>
        <Description
          id="kba-ssn-description"
          name="kba-ssn-description"
          size="lg"
        >
          <span className="inline-flex items-center justify-center gap-2">
            {t('kbaSsn.description')}
            <span className="ml-1 inline-flex items-center relative group">
              <Info className="h-5 w-5 text-gray-600 cursor-pointer" aria-hidden />
              <span className="pointer-events-none absolute left-1/2 -translate-x-[90%] sm:left-1/2 sm:-translate-x-[60%] top-full mt-2 w-max min-w-[14rem] max-w-[90vw] sm:w-72 p-2 bg-gray-700 text-white text-sm rounded-md text-left whitespace-normal break-normal opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity z-50 shadow-lg">
                {t('kbaSsn.tooltip')}
              </span>
              <span className="sr-only">{t('kbaSsn.tooltipSrLabel')}</span>
            </span>
          </span>
        </Description>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <input
            ref={inputRef}
            type={showSsn ? 'text' : 'password'}
            id="kba-ssn-input"
            name="fullSsn"
            value={showSsn ? formatSsnDisplay(ssn) : ssn}
            onChange={handleChange}
            maxLength={showSsn ? 11 : 9}
            placeholder={t('kbaSsn.placeholder')}
            className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:border-[#b3905e] focus:ring-2 focus:ring-[#b3905e]/20 transition duration-200 text-center text-2xl tracking-widest"
            required
            inputMode="numeric"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowSsn((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label={showSsn ? t('kbaSsn.hideAria') : t('kbaSsn.showAria')}
          >
            {showSsn ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>

      </div>

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('kbaSsn.firstNameLabel')}</span>
            <span className="font-medium text-gray-900">{formData.firstName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('kbaSsn.lastNameLabel')}</span>
            <span className="font-medium text-gray-900">{formData.lastName}</span>
          </div>
          {formData.address1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('kbaSsn.streetLabel')}</span>
              <span className="font-medium text-gray-900 text-right">
                {formData.address1}{formData.address2 ? `, ${formData.address2}` : ''}
              </span>
            </div>
          )}
          {formData.city && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('kbaSsn.cityLabel')}</span>
              <span className="font-medium text-gray-900">{formData.city}</span>
            </div>
          )}
          {formData.state && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('kbaSsn.stateLabel')}</span>
              <span className="font-medium text-gray-900">{formData.state}</span>
            </div>
          )}
          {formData.zipCode && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('kbaSsn.zipLabel')}</span>
              <span className="font-medium text-gray-900">{formData.zipCode}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('kbaSsn.birthDateLabel')}</span>
            <span className="font-medium text-gray-900">{formatDate(formData.birthDate)}</span>
          </div>
        </div>
      </div>

      <ErrorMessage message={error} />

      <TryAnotherWay
        show={!!error || methodExhausted}
        onChooseSms={onChooseSms}
        onChooseProspect={onChooseProspect}
      />

      {!methodExhausted && (
        <Button
          id="kba-ssn-submit"
          name="kba-ssn-submit"
          type="submit"
          disabled={loading || !isValidSsn(ssn)}
          loading={loading}
          loadingText={t('kbaSsn.verifying')}
          fullWidth
          size="lg"
        >
          {t('kbaSsn.submit')}
        </Button>
      )}

      <div className="pt-4 border-t border-gray-200">
        <div className="flex flex-col items-center gap-3">
          {onUpdatePersonalInfo && (
            <ActionLink
              id="kba-ssn-update-personal-info"
              name="kba-ssn-update-personal-info"
              onClick={onUpdatePersonalInfo}
              icon={PencilLine}
              align="center"
            >
              {t('kbaSsn.updatePersonalInfo')}
            </ActionLink>
          )}
          {onUpdateBirthDate && (
            <ActionLink
              id="kba-ssn-update-dob"
              name="kba-ssn-update-dob"
              onClick={onUpdateBirthDate}
              icon={Calendar}
              align="center"
            >
              {t('kbaSsn.updateBirthDate')}
            </ActionLink>
          )}
        </div>
      </div>
    </form>
  );
}
